// QualityTestsSection.js
// ─────────────────────────────────────────────────────────────────────────────
// Quality / materials test register for the project.
//
// Captures four families of site tests and surfaces them on the Visual Progress
// Tracker as chainage-positioned markers (see VisualProgressTracker.js, which
// imports the helpers exported from this file):
//
//   • compaction  — layer-works density (SANS 3001-NG5). Chainage RANGE marker.
//   • ndt         — radiographic / weld NDT (API 1104). POINT marker (weld).
//   • cube        — concrete cube compressive strength (SANS 5863). POINT marker.
//   • pressure    — hydrostatic pressure test + disinfection. WHOLE-ASSET band.
//
// Pass/fail is computed from a user-entered "required" value and the achieved
// value(s) on every test — nothing is hard-coded against a spec.
//
// Data:  projects/{projectId}/qualityTests
// Assets: projects/{projectId}/progressAssets   (pipeline assets, for linking
//         + chainage bounds — same collection the tracker uses)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  addDoc,
  setDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { tokens } from "./theme";

const C = tokens.color;

/* Activity layers — mirrors ACTIVITY_LABELS in VisualProgressTracker.js.
 * Used to populate the compaction "Activity layer" dropdown from the segments
 * recorded against the selected asset (collection: progressSegments). */
export const ACTIVITY_LABELS = {
  site_clearance: "Site Clearance",
  tar_cutting: "Tar Cutting",
  tar_removing: "Tar Removing",
  excavation: "Excavation",
  dump_rock: "Dump Rock",
  crusher: "Crusher",
  bedding: "Bedding",
  pipe_laying: "Pipe Laying",
  welding: "Welding",
  backfill: "Backfill",
  testing: "Testing",
  reinstatement: "Reinstatement",
};

/* ════════════════════════════════════════════════════════════════════════════
 * Shared metadata + helpers — ALSO IMPORTED BY VisualProgressTracker.js
 * ════════════════════════════════════════════════════════════════════════════ */

export const TEST_TYPES = [
  { key: "compaction", label: "Compaction", icon: "▤", locationMode: "range", color: C.info },
  { key: "ndt",        label: "NDT / Weld", icon: "◈", locationMode: "point", color: C.primaryAccent },
  { key: "cube",       label: "Concrete Cube", icon: "▣", locationMode: "point", color: C.warning },
  { key: "pressure",   label: "Pressure / Disinfection", icon: "≋", locationMode: "asset", color: C.success },
  { key: "dcp",        label: "DCP (TMH6 ST6)", icon: "↧", locationMode: "point", color: C.primary },
];

export const TEST_TYPE_META = Object.fromEntries(TEST_TYPES.map((t) => [t.key, t]));

export const RESULT_COLORS = {
  pass:    { fill: C.success, soft: C.successSoft, border: C.successBorder, label: "Pass" },
  fail:    { fill: C.danger,  soft: C.dangerSoft,  border: C.dangerBorder,  label: "Fail" },
  pending: { fill: C.warning, soft: C.warningSoft, border: C.warningBorder, label: "Pending" },
  na:      { fill: C.neutral, soft: C.neutralSoft, border: C.neutralBorder, label: "N/A" },
};

const num = (v) => {
  if (v == null) return null;
  if (typeof v === "string" && v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Relative compaction for one density position (uses entered value, else derives). */
export function positionRelativeCompaction(pos) {
  const entered = num(pos?.relativeCompaction);
  if (entered != null) return entered;
  const dry = num(pos?.dryDensity);
  const mdd = num(pos?.mdd);
  if (dry != null && mdd) return (dry / mdd) * 100;
  return null;
}

/** Mean cube strength (MPa) for a given set label ("7" or "28"). */
export function cubeAverageForSet(cube, setLabel) {
  const list = (cube?.cubes || [])
    .filter((c) => String(c.setLabel) === String(setLabel))
    .map((c) => num(c.strengthMPa) ?? (num(c.loadKN) != null ? num(c.loadKN) / (num(c.factor) || 10) : null))
    .filter((v) => v != null);
  if (!list.length) return null;
  return list.reduce((s, v) => s + v, 0) / list.length;
}

/** Mean 7-day cube strength (MPa). */
export function cubeAverage7(cube) {
  return cubeAverageForSet(cube, "7");
}

/** Mean 28-day cube strength (MPa). */
export function cubeAverage28(cube) {
  return cubeAverageForSet(cube, "28");
}

/**
 * Compute pass / fail / pending for any test, from the user-entered "required"
 * value vs the achieved value(s). Returns "pass" | "fail" | "pending".
 */
export function computeTestStatus(test) {
  if (!test) return "pending";
  switch (test.testType) {
    case "compaction": {
      const achieved = (test.compaction?.positions || [])
        .map((p) => {
          const rc = positionRelativeCompaction(p);
          const req = num(p.requiredCompaction);
          if (rc == null || req == null || !Number.isFinite(req) || req <= 0) return null;
          return { rc, req };
        })
        .filter((v) => v != null);
      if (achieved.length === 0) return "pending";
      return achieved.every((v) => v.rc >= v.req) ? "pass" : "fail";
    }
    case "ndt": {
      const r = String(test.ndt?.result || "").toLowerCase();
      if (r === "accept" || r === "accepted") return "pass";
      if (r === "reject" || r === "rejected") return "fail";
      return "pending";
    }
    case "cube": {
      const spec = num(test.cube?.specifiedStrength);
      const avg = cubeAverage28(test.cube);
      if (spec == null || avg == null) return "pending";
      return avg >= spec ? "pass" : "fail";
    }
    case "pressure": {
      const allow = num(test.pressure?.allowableLoss);
      const actual = num(test.pressure?.actualLoss);
      if (allow != null && actual != null) return actual <= allow ? "pass" : "fail";
      const r = String(test.pressure?.result || "").toLowerCase();
      if (r === "pass" || r === "passed") return "pass";
      if (r === "fail" || r === "failed") return "fail";
      return "pending";
    }
    case "dcp": {
      // In-situ CBR vs the required (specified) CBR for the layer.
      const req = num(test.dcp?.requiredCBR);
      const got = num(test.dcp?.achievedCBR);
      if (req == null || got == null) return "pending";
      return got >= req ? "pass" : "fail";
    }
    default:
      return "pending";
  }
}

export function formatChainage(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `CH ${n.toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`;
}

/** One-line location label for a test (used by register + tracker popup). */
export function testLocationLabel(test) {
  const mode = TEST_TYPE_META[test.testType]?.locationMode;
  if (mode === "range") {
    return `${formatChainage(test.chainageStart)} – ${formatChainage(test.chainageEnd)}${test.side ? ` · ${test.side}` : ""}`;
  }
  if (mode === "asset") {
    return test.assetName ? `Whole asset · ${test.assetName}` : "Whole asset";
  }
  // point
  if (test.structureRef) return `${test.structureRef}${Number.isFinite(Number(test.chainageStart)) ? ` · ${formatChainage(test.chainageStart)}` : ""}`;
  return formatChainage(test.chainageStart);
}

/**
 * Identifying key for duplicate detection. Two tests are duplicates only when
 * they share the same type AND the same reference (the "Reference" column,
 * i.e. test.title). Returns a single normalized key, or none when there is no
 * reference to match.
 */
export function duplicateKeys(test) {
  const ref = String(test.title ?? "").trim().toLowerCase();
  return ref ? [`${test.testType}|ref:${ref}`] : [];
}

/** Set of test ids that collide with at least one other test in the list. */
export function findDuplicateIds(tests) {
  const keyToIds = new Map();
  tests.forEach((t) => {
    duplicateKeys(t).forEach((k) => {
      if (!keyToIds.has(k)) keyToIds.set(k, new Set());
      keyToIds.get(k).add(t.id);
    });
  });
  const dups = new Set();
  keyToIds.forEach((ids) => {
    if (ids.size > 1) ids.forEach((id) => dups.add(id));
  });
  return dups;
}

/* Sortable columns of the Test Register. `key: null` = not sortable (actions). */
export const REGISTER_COLUMNS = [
  { label: "Type", key: "type" },
  { label: "Reference", key: "reference" },
  { label: "Location", key: "location" },
  { label: "Date", key: "date" },
  { label: "Result", key: "result" },
  { label: "Report No", key: "reportNo" },
  { label: "", key: null },
];

// Group statuses so a "Result" sort reads fail → pending → pass → n/a.
const STATUS_RANK = { fail: 0, pending: 1, pass: 2, na: 3 };

/** Comparable value for a test under the given sort key. */
function sortValue(t, key) {
  switch (key) {
    case "type":      return (TEST_TYPE_META[t.testType]?.label || t.testType || "").toLowerCase();
    case "reference": return (t.title || "").toLowerCase();
    case "location":  return testLocationLabel(t).toLowerCase();
    case "date":      return t.testDate || "";
    case "result":    return STATUS_RANK[t._status] ?? 9;
    case "reportNo":  return (t.reportNo || "").toLowerCase();
    default:          return "";
  }
}

/* ════════════════════════════════════════════════════════════════════════════
 * Small styled primitives
 * ════════════════════════════════════════════════════════════════════════════ */

const s = {
  page: { padding: 24, display: "grid", gap: 20, background: C.canvas, minHeight: "100vh", fontFamily: tokens.font.body, color: C.ink },
  header: { display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap" },
  title: { margin: 0, fontSize: 28, fontFamily: tokens.font.display, color: C.ink, letterSpacing: "-0.025em" },
  subtitle: { margin: "6px 0 0", color: C.inkMuted, fontSize: 13 },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, boxShadow: tokens.shadow.sm, minWidth: 0 },
  cardTitle: { margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: C.ink },
  statRow: { display: "flex", gap: 12, flexWrap: "wrap" },
  stat: (color) => ({ flex: "1 1 120px", minWidth: 0, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px", boxShadow: tokens.shadow.sm, borderTop: `3px solid ${color}` }),
  statLabel: { fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: C.inkSubtle },
  statValue: { display: "block", marginTop: 6, fontSize: 26, fontWeight: 800, color: C.ink },
  toolbar: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 },
  input: { width: "100%", border: `1px solid ${C.borderStrong}`, borderRadius: 6, padding: "9px 11px", fontSize: 13, outline: "none", background: C.surface, color: C.ink, boxSizing: "border-box", fontFamily: "inherit" },
  label: { display: "grid", gap: 5, fontSize: 11, fontWeight: 700, color: C.inkMuted, letterSpacing: "0.02em", minWidth: 0 },
  primaryBtn: { border: "none", borderRadius: 8, padding: "10px 16px", background: C.accent, color: C.inkOnAccent, fontWeight: 700, cursor: "pointer", fontSize: 13 },
  ghostBtn: { border: `1px solid ${C.borderStrong}`, borderRadius: 7, padding: "8px 12px", background: C.surface, color: C.ink, fontWeight: 600, cursor: "pointer", fontSize: 12 },
  dangerBtn: { border: `1px solid ${C.dangerBorder}`, borderRadius: 7, padding: "6px 10px", background: C.dangerSoft, color: C.danger, fontWeight: 700, cursor: "pointer", fontSize: 12 },
  empty: { padding: 22, borderRadius: 10, background: C.surfaceAlt, color: C.inkMuted, textAlign: "center", fontSize: 13 },
  tableWrap: { overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 10 },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 760 },
  th: { textAlign: "left", padding: "10px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.inkSubtle, borderBottom: `2px solid ${C.border}`, whiteSpace: "nowrap", background: C.surfaceAlt },
  td: { padding: "10px 12px", fontSize: 13, color: C.ink, borderBottom: `1px solid ${C.border}`, verticalAlign: "top" },
  pill: (r) => ({ display: "inline-block", padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: RESULT_COLORS[r].soft, color: RESULT_COLORS[r].fill, border: `1px solid ${RESULT_COLORS[r].border}`, whiteSpace: "nowrap" }),
  typeChip: (color) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: C.surfaceAlt, color: C.ink, border: `1px solid ${C.border}` }),
  dupRow: { background: C.dangerSoft, boxShadow: `inset 3px 0 0 ${C.danger}` },
  dupNote: { display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4, padding: "2px 7px", borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", background: C.dangerSoft, color: C.danger, border: `1px solid ${C.dangerBorder}` },
};

function Field({ label, children }) {
  return (
    <label style={s.label}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function TextInput(props) {
  return <input {...props} style={{ ...s.input, ...(props.style || {}) }} />;
}

/* ════════════════════════════════════════════════════════════════════════════
 * Main section
 * ════════════════════════════════════════════════════════════════════════════ */

export default function QualityTestsSection({ currentProjectId, currentUser }) {
  const projectId = currentProjectId || "";

  const [tests, setTests] = useState([]);
  const [assets, setAssets] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const [filterType, setFilterType] = useState("all");
  const [filterResult, setFilterResult] = useState("all");
  const [filterDup, setFilterDup] = useState(false);
  const [search, setSearch] = useState("");

  // Column sort — default to most-recent date first.
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "date" ? "desc" : "asc"); // dates read newest-first
    }
  };

  // ── Subscriptions ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(collection(db, "projects", projectId, "qualityTests"), (snap) => {
      setTests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(collection(db, "projects", projectId, "progressAssets"), (snap) => {
      setAssets(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [projectId]);


  // ── Derived ──────────────────────────────────────────────────────────────--
  const withStatus = useMemo(
    () => tests.map((t) => ({ ...t, _status: computeTestStatus(t) })),
    [tests]
  );

  // Duplicate detection runs across ALL tests (not just the filtered view) so a
  // row stays flagged even when its twin is hidden by a filter or search.
  const duplicateIds = useMemo(() => findDuplicateIds(withStatus), [withStatus]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const dir = sortDir === "asc" ? 1 : -1;
    return withStatus
      .filter((t) => (filterType === "all" ? true : t.testType === filterType))
      .filter((t) => (filterResult === "all" ? true : t._status === filterResult))
      .filter((t) => (filterDup ? duplicateIds.has(t.id) : true))
      .filter((t) => {
        if (!q) return true;
        return [t.title, t.reportNo, t.lab, t.assetName, t.structureRef, testLocationLabel(t)]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
      .sort((a, b) => {
        const cmp = String(sortValue(a, sortKey)).localeCompare(
          String(sortValue(b, sortKey)),
          undefined,
          { numeric: true, sensitivity: "base" }
        );
        return cmp * dir;
      });
  }, [withStatus, filterType, filterResult, filterDup, duplicateIds, search, sortKey, sortDir]);

  const counts = useMemo(() => {
    const c = { total: withStatus.length, pass: 0, fail: 0, pending: 0 };
    withStatus.forEach((t) => { c[t._status] = (c[t._status] || 0) + 1; });
    return c;
  }, [withStatus]);

  // ── Persistence ──────────────────────────────────────────────────────────--
  async function logAudit(action, entityId, summary) {
    try {
      await addDoc(collection(db, "projects", projectId, "auditLogs"), {
        entityType: "qualityTest",
        entityId: entityId || "",
        action,
        summary: summary || "",
        userName: currentUser?.displayName || "",
        userEmail: currentUser?.email || "",
        createdAt: serverTimestamp(),
      });
    } catch (e) { /* best-effort */ }
  }

  async function saveTest(payload, id) {
    if (!projectId) return;
    const status = computeTestStatus(payload);
    const data = { ...payload, projectId, status, updatedAt: serverTimestamp() };
    if (id) {
      await setDoc(doc(db, "projects", projectId, "qualityTests", id), data, { merge: true });
      await logAudit("update", id, `${TEST_TYPE_META[payload.testType]?.label} test updated (${payload.title || testLocationLabel(payload)})`);
    } else {
      const ref = await addDoc(collection(db, "projects", projectId, "qualityTests"), {
        ...data,
        createdAt: serverTimestamp(),
      });
      await logAudit("create", ref.id, `${TEST_TYPE_META[payload.testType]?.label} test captured (${payload.title || testLocationLabel(payload)})`);
    }
    setModalOpen(false);
    setEditing(null);
  }

  async function removeTest(t) {
    if (!projectId) return;
    if (!window.confirm(`Delete this ${TEST_TYPE_META[t.testType]?.label || ""} test? This cannot be undone.`)) return;
    await deleteDoc(doc(db, "projects", projectId, "qualityTests", t.id));
    await logAudit("delete", t.id, `${TEST_TYPE_META[t.testType]?.label} test deleted (${t.title || testLocationLabel(t)})`);
  }

  if (!projectId) {
    return (
      <div style={s.page}>
        <section style={s.card}>
          <h1 style={s.title}>Quality Tests</h1>
          <div style={{ ...s.empty, marginTop: 16 }}>Select a project first to capture and view tests.</div>
        </section>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={{ minWidth: 0 }}>
          <h1 style={s.title}>Quality Tests</h1>
          <p style={s.subtitle}>
            Compaction, NDT, concrete cube and pressure tests — markers appear on the Visual Progress Tracker.
          </p>
        </div>
        <button
          style={s.primaryBtn}
          onClick={() => { setEditing(null); setModalOpen(true); }}
        >
          + Capture Test
        </button>
      </div>

      <div style={s.statRow}>
        <div style={s.stat(C.primary)}><span style={s.statLabel}>Total</span><strong style={s.statValue}>{counts.total}</strong></div>
        <div style={s.stat(C.success)}><span style={s.statLabel}>Pass</span><strong style={s.statValue}>{counts.pass}</strong></div>
        <div style={s.stat(C.danger)}><span style={s.statLabel}>Fail</span><strong style={s.statValue}>{counts.fail}</strong></div>
        <div style={s.stat(C.warning)}><span style={s.statLabel}>Pending</span><strong style={s.statValue}>{counts.pending}</strong></div>
      </div>

      <section style={s.card}>
        <h2 style={s.cardTitle}>Test Register</h2>

        <div style={s.toolbar}>
          <input
            style={{ ...s.input, maxWidth: 240 }}
            placeholder="Search ref, lab, location…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select style={{ ...s.input, width: "auto" }} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All types</option>
            {TEST_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <select style={{ ...s.input, width: "auto" }} value={filterResult} onChange={(e) => setFilterResult(e.target.value)}>
            <option value="all">All results</option>
            <option value="pass">Pass</option>
            <option value="fail">Fail</option>
            <option value="pending">Pending</option>
          </select>
          {duplicateIds.size > 0 && (
            <button
              style={{
                ...s.ghostBtn,
                borderColor: filterDup ? C.dangerBorder : C.borderStrong,
                background: filterDup ? C.dangerSoft : C.surface,
                color: filterDup ? C.danger : C.ink,
                fontWeight: 700,
              }}
              onClick={() => setFilterDup((v) => !v)}
              title="Show only entries flagged as possible duplicates"
            >
              ⚠ Duplicates ({duplicateIds.size})
            </button>
          )}
          {(search || filterType !== "all" || filterResult !== "all" || filterDup) && (
            <button style={s.ghostBtn} onClick={() => { setSearch(""); setFilterType("all"); setFilterResult("all"); setFilterDup(false); }}>
              Clear
            </button>
          )}
        </div>

        {filtered.length === 0 ? (
          <div style={s.empty}>
            {withStatus.length === 0
              ? "No tests captured yet. Use “Capture Test” to add the first one."
              : "No tests match the current filters."}
          </div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  {REGISTER_COLUMNS.map((col) => {
                    const sortable = !!col.key;
                    const active = sortable && sortKey === col.key;
                    return (
                      <th
                        key={col.label || "actions"}
                        style={{
                          ...s.th,
                          cursor: sortable ? "pointer" : "default",
                          userSelect: "none",
                          color: active ? C.ink : s.th.color,
                        }}
                        onClick={sortable ? () => toggleSort(col.key) : undefined}
                        title={sortable ? `Sort by ${col.label}` : undefined}
                      >
                        {col.label}
                        {sortable && (
                          <span style={{ marginLeft: 6, fontSize: 9, opacity: active ? 1 : 0.3 }}>
                            {active ? (sortDir === "asc" ? "▲" : "▼") : "▾"}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => {
                  const meta = TEST_TYPE_META[t.testType] || {};
                  const isDup = duplicateIds.has(t.id);
                  return (
                    <tr key={t.id} style={isDup ? s.dupRow : undefined}>
                      <td style={s.td}>
                        <span style={s.typeChip(meta.color)}>
                          <span style={{ color: meta.color }}>{meta.icon}</span>{meta.label || t.testType}
                        </span>
                      </td>
                      <td style={s.td}>
                        <div style={{ fontWeight: 700, color: isDup ? C.danger : C.ink }}>{t.title || "—"}</div>
                        {t.lab && <div style={{ fontSize: 11, color: C.inkSubtle }}>{t.lab}</div>}
                        {isDup && (
                          <div
                            style={s.dupNote}
                            title="Possible duplicate — another entry of the same type shares this reference."
                          >
                            ⚠ Duplicate
                          </div>
                        )}
                      </td>
                      <td style={{ ...s.td, color: C.inkMuted }}>{testLocationLabel(t)}</td>
                      <td style={{ ...s.td, whiteSpace: "nowrap" }}>{t.testDate || "—"}</td>
                      <td style={s.td}><span style={s.pill(t._status)}>{RESULT_COLORS[t._status].label}</span></td>
                      <td style={{ ...s.td, fontFamily: tokens.font.mono, fontSize: 12 }}>{t.reportNo || "—"}</td>
                      <td style={s.td}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button style={s.ghostBtn} onClick={() => setViewing(t)}>View</button>
                          <button style={s.ghostBtn} onClick={() => { setEditing(t); setModalOpen(true); }}>Edit</button>
                          <button style={s.dangerBtn} onClick={() => removeTest(t)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>


      {modalOpen && (
        <TestFormModal
          assets={assets}
          existing={editing}
          onClose={() => { setModalOpen(false); setEditing(null); }}
          onSave={saveTest}
        />
      )}

      {viewing && (
        <TestViewModal
          test={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
 * Capture / edit modal — keeps its own state so typing never re-renders the
 * section (matches the VOFormModal isolation pattern).
 * ════════════════════════════════════════════════════════════════════════════ */

export function blankForm(testType) {
  const firstPos = emptyPosition([], "");
  return {
    testType,
    title: "",
    testDate: new Date().toISOString().slice(0, 10),
    lab: "",
    reportNo: "",
    technician: "",
    documentRef: "",
    notes: "",
    assetId: "",
    assetName: "",
    side: "",
    structureRef: "",
    chainageStart: "",
    chainageEnd: "",
    compaction: { spec: "SANS 3001-NG5", positions: [firstPos] },
    ndt: { method: "RT", weldNo: "", welder: "", pipeOD: "", acceptanceStandard: "API 1104", weldingProcess: "", materialSpec: "", result: "", defects: "" },
    cube: { pourRef: "", element: "", specifiedStrength: "", dateCast: "", supplier: "", factor: 10, cubes: [emptyCube("7"), emptyCube("28")] },
    pressure: { medium: "Water", testPressure: "", units: "kPa", durationMinutes: "", allowableLoss: "", actualLoss: "", result: "", disinfectionDate: "", disinfectionMethod: "", bacteriologicalResult: "", clearanceDate: "" },
    dcp: { method: "TMH6: ST6", soilType: "", soilCondition: "", layerLevel: "", requiredCBR: "", achievedCBR: "", dn: "", bearingCapacity: "" },
  };
}

/* ─── Compaction layer definitions ─────────────────────────────────────────
 * Each layer type carries its own default required compaction % so the user
 * doesn't have to re-type it every row. All values are editable per-row.
 * ─────────────────────────────────────────────────────────────────────────── */
export const COMPACTION_LAYERS = [
  { key: "bedding",       label: "Bedding",          defaultRequired: 90 },
  { key: "blanket_l1",    label: "Blanket Layer 1",   defaultRequired: 90 },
  { key: "blanket_l2",    label: "Blanket Layer 2",   defaultRequired: 90 },
  { key: "blanket_l3",    label: "Blanket Layer 3",   defaultRequired: 90 },
  { key: "blanket_l4",    label: "Blanket Layer 4",   defaultRequired: 90 },
  { key: "backfill_l1",   label: "Backfill Layer 1",  defaultRequired: 93 },
  { key: "backfill_l2",   label: "Backfill Layer 2",  defaultRequired: 93 },
  { key: "backfill_l3",   label: "Backfill Layer 3",  defaultRequired: 93 },
  { key: "backfill_l4",   label: "Backfill Layer 4",  defaultRequired: 93 },
  { key: "backfill_l5",   label: "Backfill Layer 5",  defaultRequired: 93 },
  { key: "subbase",       label: "Sub-base",          defaultRequired: 98 },
  { key: "basecourse",    label: "Base Course",       defaultRequired: 100 },
  { key: "dump_rock",     label: "Dump Rock",         defaultRequired: 0 },
  { key: "other",         label: "Other",             defaultRequired: 90 },
];
const LAYER_META = Object.fromEntries(COMPACTION_LAYERS.map((l) => [l.key, l]));

/** Auto-generate a test number string: CT-001, CT-002, … */
function nextTestNo(positions) {
  const max = positions.reduce((acc, p) => {
    const m = String(p.testNo || "").match(/(\d+)$/);
    return m ? Math.max(acc, parseInt(m[1], 10)) : acc;
  }, 0);
  return `CT-${String(max + 1).padStart(3, "0")}`;
}

export function emptyPosition(positions = [], chainage = "") {
  return {
    testNo: nextTestNo(positions),
    chainage,
    layerKey: "bedding",
    depth: "",
    requiredCompaction: LAYER_META["bedding"].defaultRequired,
    wetDensity: "", dryDensity: "", moistureCorrected: "", mdd: "", omc: "",
    relativeCompaction: "",
  };
}
export function emptyCube(setLabel) {
  return { setLabel, cubeId: "", ageDays: setLabel, loadKN: "", factor: 10, strengthMPa: "" };
}

function TestFormModal({ assets, existing, onClose, onSave }) {
  const [compactionRowsOpen, setCompactionRowsOpen] = useState(false);

  const [form, setForm] = useState(() => {
    if (existing) {
      const base = blankForm(existing.testType || "compaction");
      return {
        ...base,
        ...existing,
        compaction: { ...base.compaction, ...(existing.compaction || {}), positions: (existing.compaction?.positions?.length ? existing.compaction.positions : base.compaction.positions) },
        ndt: { ...base.ndt, ...(existing.ndt || {}) },
        cube: { ...base.cube, ...(existing.cube || {}), cubes: (existing.cube?.cubes?.length ? existing.cube.cubes : base.cube.cubes) },
        pressure: { ...base.pressure, ...(existing.pressure || {}) },
        dcp: { ...base.dcp, ...(existing.dcp || {}) },
      };
    }
    return blankForm("compaction");
  });

  const meta = TEST_TYPE_META[form.testType];
  const mode = meta?.locationMode;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setSub = (key, patch) => setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));

  function chooseType(testType) {
    // preserve common header fields, reset the type payload
    setForm((f) => ({ ...blankForm(testType), title: f.title, testDate: f.testDate, lab: f.lab, reportNo: f.reportNo, technician: f.technician, documentRef: f.documentRef, notes: f.notes, assetId: f.assetId, assetName: f.assetName }));
  }

  function selectAsset(id) {
    const a = assets.find((x) => x.id === id);
    set({ assetId: id, assetName: a?.assetName || a?.name || "" });
  }

  // compaction positions — functional updaters so they always read the latest
  // state (closure-captured arrays were the cause of "Add position" no-ops).
  const positions = form.compaction.positions || [];
  const setPosition = (i, patch) =>
    setForm((f) => {
      const list = f.compaction.positions || [];
      return { ...f, compaction: { ...f.compaction, positions: list.map((p, idx) => (idx === i ? { ...p, ...patch } : p)) } };
    });
  const addPosition = (chainage = "") =>
    setForm((f) => {
      const list = f.compaction.positions || [];
      return { ...f, compaction: { ...f.compaction, positions: [...list, emptyPosition(list, chainage)] } };
    });
  const removePosition = (i) =>
    setForm((f) => {
      const list = f.compaction.positions || [];
      return { ...f, compaction: { ...f.compaction, positions: list.filter((_, idx) => idx !== i) } };
    });

  // cubes — one 7-day and one 28-day result, captured directly (no dynamic rows)
  const getCubeSet = (setLabel) =>
    (form.cube.cubes || []).find((c) => String(c.setLabel) === setLabel) || emptyCube(setLabel);
  const setCubeSet = (setLabel, patch) =>
    setForm((f) => {
      const list = [...(f.cube.cubes || [])];
      const idx = list.findIndex((c) => String(c.setLabel) === setLabel);
      if (idx === -1) list.push({ ...emptyCube(setLabel), ...patch });
      else list[idx] = { ...list[idx], ...patch };
      return { ...f, cube: { ...f.cube, cubes: list } };
    });

  // live preview of result
  const previewStatus = useMemo(() => computeTestStatus(normalizeForm(form)), [form]);

  function handleSave() {
    const payload = normalizeForm(form);
    // minimal validation
    if (mode === "range" && (payload.chainageStart === null || payload.chainageEnd === null)) {
      alert("Enter a start and end chainage for the tested range.");
      return;
    }
    if (mode === "asset" && !payload.assetId) {
      alert("Pressure tests must be linked to a pipeline asset.");
      return;
    }
    onSave(payload, existing?.id);
  }

  const pipelineAssets = assets.filter((a) => String(a.assetType || "pipeline").toLowerCase() === "pipeline");

  return createPortal(
    <div style={m.overlay} onClick={onClose}>
      <div style={m.dialog} onClick={(e) => e.stopPropagation()}>
        <div style={m.head}>
          <div>
            <div style={m.eyebrow}>{existing ? "Edit test" : "Capture test"}</div>
            <h2 style={m.title}>{meta?.label} test</h2>
          </div>
          <button type="button" style={s.ghostBtn} onClick={onClose}>Close</button>
        </div>

        <div style={m.body}>
          {/* Type selector */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
            {TEST_TYPES.map((t) => {
              const active = t.key === form.testType;
              return (
                <button
                  type="button"
                  key={t.key}
                  onClick={() => chooseType(t.key)}
                  style={{
                    ...s.ghostBtn,
                    borderColor: active ? C.accent : C.borderStrong,
                    background: active ? C.accentSoft : C.surface,
                    color: active ? C.accentHover : C.ink,
                    fontWeight: 700,
                  }}
                >
                  <span style={{ marginRight: 6 }}>{t.icon}</span>{t.label}
                </button>
              );
            })}
          </div>

          {/* Common header */}
          <div style={m.grid3}>
            <Field label="Reference / Title"><TextInput value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. FW90 RT / SV530 bedding" /></Field>
            <Field label="Test date"><TextInput type="date" value={form.testDate} onChange={(e) => set({ testDate: e.target.value })} /></Field>
            <Field label="Report no"><TextInput value={form.reportNo} onChange={(e) => set({ reportNo: e.target.value })} placeholder="e.g. IC-NDT-RT-…" /></Field>
            <Field label="Lab / issuer"><TextInput value={form.lab} onChange={(e) => set({ lab: e.target.value })} placeholder="e.g. Roadlab / IC-NDT" /></Field>
            <Field label="Technician / interpreter"><TextInput value={form.technician} onChange={(e) => set({ technician: e.target.value })} /></Field>
            <Field label="Document reference / link"><TextInput value={form.documentRef} onChange={(e) => set({ documentRef: e.target.value })} placeholder="filename or URL" /></Field>
          </div>

          {/* Location block */}
          <SectionLabel>Location</SectionLabel>
          {mode === "range" && (
            <div style={m.grid3}>
              <Field label="Pipeline asset">
                <select style={s.input} value={form.assetId} onChange={(e) => selectAsset(e.target.value)}>
                  <option value="">— select —</option>
                  {pipelineAssets.map((a) => <option key={a.id} value={a.id}>{a.assetName || a.name}</option>)}
                </select>
              </Field>
              <Field label="Side / line"><TextInput value={form.side} onChange={(e) => set({ side: e.target.value })} placeholder="e.g. Southern Side" /></Field>
              <Field label="Chainage start (m)"><TextInput type="number" value={form.chainageStart} onChange={(e) => set({ chainageStart: e.target.value })} /></Field>
              <Field label="Chainage end (m)"><TextInput type="number" value={form.chainageEnd} onChange={(e) => set({ chainageEnd: e.target.value })} /></Field>
            </div>
          )}
          {mode === "point" && (
            <div style={m.grid3}>
              <Field label="Pipeline asset (optional)">
                <select style={s.input} value={form.assetId} onChange={(e) => selectAsset(e.target.value)}>
                  <option value="">— none —</option>
                  {pipelineAssets.map((a) => <option key={a.id} value={a.id}>{a.assetName || a.name}</option>)}
                </select>
              </Field>
              <Field label="Chainage (m)"><TextInput type="number" value={form.chainageStart} onChange={(e) => set({ chainageStart: e.target.value })} placeholder="position on the bar" /></Field>
              <Field label="Structure / chamber ref"><TextInput value={form.structureRef} onChange={(e) => set({ structureRef: e.target.value })} placeholder="e.g. Chamber CH-01" /></Field>
            </div>
          )}
          {mode === "asset" && (
            <div style={m.grid3}>
              <Field label="Pipeline asset">
                <select style={s.input} value={form.assetId} onChange={(e) => selectAsset(e.target.value)}>
                  <option value="">— select —</option>
                  {pipelineAssets.map((a) => <option key={a.id} value={a.id}>{a.assetName || a.name}</option>)}
                </select>
              </Field>
            </div>
          )}

          {/* Type-specific */}
          {form.testType === "compaction" && (
            <>
              <SectionLabel>Compaction result</SectionLabel>
              <div style={m.grid3}>
                <Field label="Standard"><TextInput value={form.compaction.spec} onChange={(e) => setSub("compaction", { spec: e.target.value })} /></Field>
              </div>

              <div style={m.compactionSummaryCard}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 800, color: C.ink }}>Compaction result rows</div>
                    <div style={{ fontSize: 12, color: C.inkMuted }}>
                      {positions.length} result{positions.length === 1 ? "" : "s"} across{" "}
                      {new Set(positions.map((p) => p.chainage).filter(Boolean)).size} chainage{new Set(positions.map((p) => p.chainage).filter(Boolean)).size === 1 ? "" : "s"}
                      {(() => {
                        const values = positions.map(positionRelativeCompaction).filter((v) => v != null);
                        if (!values.length) return "";
                        return ` · min ${Math.min(...values).toFixed(1)}% / max ${Math.max(...values).toFixed(1)}%`;
                      })()}
                    </div>
                  </div>
                  <button type="button" style={s.primaryBtn} onClick={() => setCompactionRowsOpen(true)}>
                    Open compaction rows
                  </button>
                </div>

                {/* Preview — first 5, grouped by chainage */}
                <div style={{ ...s.tableWrap, marginTop: 10 }}>
                  <table style={{ ...s.table, minWidth: 540 }}>
                    <thead>
                      <tr>{["Test No", "CH", "Layer", "Required %", "Achieved %", "Status"].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {positions.slice(0, 5).map((p, i) => {
                        const rc = positionRelativeCompaction(p);
                        const req = num(p.requiredCompaction);
                        const bad = rc != null && req != null && Number.isFinite(req) && req > 0 && rc < req;
                        return (
                          <tr key={i}>
                            <td style={{ ...s.td, fontFamily: tokens.font.mono, fontSize: 12 }}>{p.testNo || "—"}</td>
                            <td style={s.td}>{p.chainage ? `CH ${p.chainage}` : "—"}</td>
                            <td style={s.td}>{LAYER_META[p.layerKey]?.label || p.layerKey || "—"}</td>
                            <td style={s.td}>{req != null ? `${req}%` : "—"}</td>
                            <td style={s.td}>{rc != null ? `${rc.toFixed(1)}%` : "—"}</td>
                            <td style={s.td}>{rc == null || req == null ? "Pending" : bad ? "Below req." : "OK"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {positions.length > 5 && <p style={m.hint}>Showing first 5 rows. Open the popup to view and edit all rows.</p>}
              </div>
              <p style={m.hint}>Each layer row carries its own required %. Pass = every row ≥ its required %.</p>

              {compactionRowsOpen && (
                <CompactionRowsModal
                  positions={positions}
                  setPosition={setPosition}
                  addPosition={addPosition}
                  removePosition={removePosition}
                  onClose={() => setCompactionRowsOpen(false)}
                />
              )}
            </>
          )}

          {form.testType === "ndt" && (
            <>
              <SectionLabel>NDT result</SectionLabel>
              <div style={m.grid3}>
                <Field label="Method">
                  <select style={s.input} value={form.ndt.method} onChange={(e) => setSub("ndt", { method: e.target.value })}>
                    {["RT", "UT", "MPI", "DPI", "PAUT", "Visual"].map((x) => <option key={x}>{x}</option>)}
                  </select>
                </Field>
                <Field label="Weld / joint no"><TextInput value={form.ndt.weldNo} onChange={(e) => setSub("ndt", { weldNo: e.target.value })} placeholder="e.g. FW 90" /></Field>
                <Field label="Welder ID"><TextInput value={form.ndt.welder} onChange={(e) => setSub("ndt", { welder: e.target.value })} placeholder="e.g. VM03/ZM02" /></Field>
                <Field label="Pipe OD / size"><TextInput value={form.ndt.pipeOD} onChange={(e) => setSub("ndt", { pipeOD: e.target.value })} placeholder="e.g. 864 OD" /></Field>
                <Field label="Acceptance standard"><TextInput value={form.ndt.acceptanceStandard} onChange={(e) => setSub("ndt", { acceptanceStandard: e.target.value })} /></Field>
                <Field label="Welding process"><TextInput value={form.ndt.weldingProcess} onChange={(e) => setSub("ndt", { weldingProcess: e.target.value })} placeholder="e.g. SMAW" /></Field>
                <Field label="Material spec"><TextInput value={form.ndt.materialSpec} onChange={(e) => setSub("ndt", { materialSpec: e.target.value })} placeholder="e.g. X52" /></Field>
                <Field label="Result">
                  <select style={s.input} value={form.ndt.result} onChange={(e) => setSub("ndt", { result: e.target.value })}>
                    <option value="">— pending —</option>
                    <option value="accept">Accept</option>
                    <option value="reject">Reject</option>
                  </select>
                </Field>
              </div>
              <Field label="Defects / remarks"><TextInput value={form.ndt.defects} onChange={(e) => setSub("ndt", { defects: e.target.value })} placeholder="e.g. GP @ 209 (2.5mm)" /></Field>
            </>
          )}

          {form.testType === "cube" && (
            <>
              <SectionLabel>Concrete cubes</SectionLabel>
              <div style={m.grid3}>
                <Field label="Pour reference"><TextInput value={form.cube.pourRef} onChange={(e) => setSub("cube", { pourRef: e.target.value })} /></Field>
                <Field label="Element / location"><TextInput value={form.cube.element} onChange={(e) => setSub("cube", { element: e.target.value })} placeholder="e.g. joint sheathing FW38-40" /></Field>
                <Field label="Specified strength (MPa)"><TextInput type="number" value={form.cube.specifiedStrength} onChange={(e) => setSub("cube", { specifiedStrength: e.target.value })} placeholder="e.g. 31" /></Field>
                <Field label="Date cast"><TextInput type="date" value={form.cube.dateCast} onChange={(e) => setSub("cube", { dateCast: e.target.value })} /></Field>
                <Field label="Supplier"><TextInput value={form.cube.supplier} onChange={(e) => setSub("cube", { supplier: e.target.value })} /></Field>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginTop: 8 }}>
                {["7", "28"].map((setLabel) => {
                  const c = getCubeSet(setLabel);
                  const derived = num(c.strengthMPa) ?? (num(c.loadKN) != null ? num(c.loadKN) / (num(c.factor) || 10) : null);
                  return (
                    <div key={setLabel} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, background: C.surfaceAlt }}>
                      <SectionLabel>{setLabel}-day result</SectionLabel>
                      <Field label="Cube / lab ID">
                        <TextInput value={c.cubeId || ""} onChange={(e) => setCubeSet(setLabel, { cubeId: e.target.value })} placeholder="26/CO/…" />
                      </Field>
                      <Field label="Load (kN)">
                        <TextInput type="number" value={c.loadKN || ""} onChange={(e) => setCubeSet(setLabel, { loadKN: e.target.value })} placeholder="optional" />
                      </Field>
                      <Field label="Strength (MPa)">
                        <TextInput type="number" value={c.strengthMPa || ""} onChange={(e) => setCubeSet(setLabel, { strengthMPa: e.target.value })}
                          placeholder={derived != null ? `${derived.toFixed(1)} (from load)` : "auto"} />
                      </Field>
                    </div>
                  );
                })}
              </div>
              <p style={m.hint}>Capture one 7-day and one 28-day result. Strength auto-derives from Load ÷ 10 (100 mm cube) when left blank. Pass = 28-day strength ≥ specified.</p>
            </>
          )}

          {form.testType === "pressure" && (
            <>
              <SectionLabel>Pressure test &amp; disinfection</SectionLabel>
              <div style={m.grid3}>
                <Field label="Medium"><TextInput value={form.pressure.medium} onChange={(e) => setSub("pressure", { medium: e.target.value })} /></Field>
                <Field label="Test pressure"><TextInput type="number" value={form.pressure.testPressure} onChange={(e) => setSub("pressure", { testPressure: e.target.value })} /></Field>
                <Field label="Units">
                  <select style={s.input} value={form.pressure.units} onChange={(e) => setSub("pressure", { units: e.target.value })}>
                    {["kPa", "bar", "m head", "psi"].map((x) => <option key={x}>{x}</option>)}
                  </select>
                </Field>
                <Field label="Duration (min)"><TextInput type="number" value={form.pressure.durationMinutes} onChange={(e) => setSub("pressure", { durationMinutes: e.target.value })} /></Field>
                <Field label="Allowable loss"><TextInput type="number" value={form.pressure.allowableLoss} onChange={(e) => setSub("pressure", { allowableLoss: e.target.value })} placeholder="same units" /></Field>
                <Field label="Actual loss"><TextInput type="number" value={form.pressure.actualLoss} onChange={(e) => setSub("pressure", { actualLoss: e.target.value })} placeholder="same units" /></Field>
                <Field label="Disinfection date"><TextInput type="date" value={form.pressure.disinfectionDate} onChange={(e) => setSub("pressure", { disinfectionDate: e.target.value })} /></Field>
                <Field label="Disinfection method"><TextInput value={form.pressure.disinfectionMethod} onChange={(e) => setSub("pressure", { disinfectionMethod: e.target.value })} placeholder="e.g. chlorination 50 ppm" /></Field>
                <Field label="Bacteriological result"><TextInput value={form.pressure.bacteriologicalResult} onChange={(e) => setSub("pressure", { bacteriologicalResult: e.target.value })} placeholder="e.g. SANS 241 pass" /></Field>
                <Field label="Clearance date"><TextInput type="date" value={form.pressure.clearanceDate} onChange={(e) => setSub("pressure", { clearanceDate: e.target.value })} /></Field>
              </div>
              <p style={m.hint}>Pass = actual loss ≤ allowable loss. Leave both blank to set the result manually below.</p>
              {(num(form.pressure.allowableLoss) == null || num(form.pressure.actualLoss) == null) && (
                <Field label="Manual result">
                  <select style={{ ...s.input, maxWidth: 200 }} value={form.pressure.result} onChange={(e) => setSub("pressure", { result: e.target.value })}>
                    <option value="">—</option>
                    <option value="pass">Pass</option>
                    <option value="fail">Fail</option>
                  </select>
                </Field>
              )}
            </>
          )}

          {form.testType === "dcp" && (
            <>
              <SectionLabel>DCP — in-situ layer strength</SectionLabel>
              <div style={m.grid3}>
                <Field label="Method"><TextInput value={form.dcp.method} onChange={(e) => setSub("dcp", { method: e.target.value })} placeholder="TMH6: ST6" /></Field>
                <Field label="Soil type">
                  <select style={s.input} value={form.dcp.soilType} onChange={(e) => setSub("dcp", { soilType: e.target.value })}>
                    {["", "Granular", "Cohesive", "Gravel", "Sand", "Mixed", "Other"].map((x) => <option key={x} value={x}>{x || "—"}</option>)}
                  </select>
                </Field>
                <Field label="Soil condition">
                  <select style={s.input} value={form.dcp.soilCondition} onChange={(e) => setSub("dcp", { soilCondition: e.target.value })}>
                    {["", "Dry", "Moist", "Wet", "Wet/Soaked", "Saturated"].map((x) => <option key={x} value={x}>{x || "—"}</option>)}
                  </select>
                </Field>
                <Field label="Layer / level"><TextInput value={form.dcp.layerLevel} onChange={(e) => setSub("dcp", { layerLevel: e.target.value })} placeholder="e.g. Before Bedding, Subgrade" /></Field>
                <Field label="Required CBR (%)"><TextInput type="number" value={form.dcp.requiredCBR} onChange={(e) => setSub("dcp", { requiredCBR: e.target.value })} placeholder="e.g. 15" /></Field>
                <Field label="Achieved CBR (%)"><TextInput type="number" value={form.dcp.achievedCBR} onChange={(e) => setSub("dcp", { achievedCBR: e.target.value })} placeholder="representative in-situ" /></Field>
                <Field label="DN (mm/blow)"><TextInput type="number" value={form.dcp.dn} onChange={(e) => setSub("dcp", { dn: e.target.value })} placeholder="optional" /></Field>
                <Field label="Bearing capacity (kPa)"><TextInput type="number" value={form.dcp.bearingCapacity} onChange={(e) => setSub("dcp", { bearingCapacity: e.target.value })} placeholder="optional" /></Field>
              </div>
              <p style={m.hint}>Pass = achieved CBR ≥ required CBR. Enter the representative in-situ values from the DCP report (TMH6 ST6 / Paige-Green). Use the location fields above for the test position (TP) and chainage.</p>
            </>
          )}

          <SectionLabel>Notes</SectionLabel>
          <textarea
            style={{ ...s.input, minHeight: 64, resize: "vertical" }}
            value={form.notes}
            onChange={(e) => set({ notes: e.target.value })}
            placeholder="Optional notes, deviations, instructions…"
          />
        </div>

        <div style={m.foot}>
          <span style={s.pill(previewStatus)}>Result preview: {RESULT_COLORS[previewStatus].label}</span>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" style={s.ghostBtn} onClick={onClose}>Cancel</button>
            <button type="button" style={s.primaryBtn} onClick={handleSave}>{existing ? "Save changes" : "Capture test"}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}


function DetailRow({ label, value }) {
  return (
    <div style={m.detailRow}>
      <div style={m.detailLabel}>{label}</div>
      <div style={m.detailValue}>{value == null || value === "" ? "—" : value}</div>
    </div>
  );
}

function TestViewModal({ test, onClose }) {
  const status = computeTestStatus(test);
  const meta = TEST_TYPE_META[test.testType] || {};
  const positions = test.compaction?.positions || [];
  const cubes = test.cube?.cubes || [];

  return createPortal(
    <div style={{ ...m.overlay, zIndex: (tokens.z.modal || 1000) + 4 }} onClick={onClose}>
      <div style={m.wideDialog} onClick={(e) => e.stopPropagation()}>
        <div style={m.head}>
          <div>
            <div style={m.eyebrow}>Test summary</div>
            <h2 style={m.title}>{test.title || meta.label || "Quality test"}</h2>
          </div>
          <button type="button" style={s.ghostBtn} onClick={onClose}>Close</button>
        </div>

        <div style={m.body}>
          <div style={m.summaryGrid}>
            <DetailRow label="Type" value={meta.label || test.testType} />
            <DetailRow label="Result" value={<span style={s.pill(status)}>{RESULT_COLORS[status].label}</span>} />
            <DetailRow label="Date" value={test.testDate} />
            <DetailRow label="Report No" value={test.reportNo} />
            <DetailRow label="Lab / Issuer" value={test.lab} />
            <DetailRow label="Technician" value={test.technician} />
            <DetailRow label="Location" value={testLocationLabel(test)} />
            <DetailRow label="Document Ref" value={test.documentRef} />
          </div>

          {test.testType === "compaction" && (
            <>
              <SectionLabel>Compaction results</SectionLabel>
              <div style={m.summaryGrid}>
                <DetailRow label="Standard" value={test.compaction?.spec} />
                <DetailRow label="Chainages tested" value={[...new Set((test.compaction?.positions || []).map((p) => p.chainage).filter(Boolean))].map((c) => `CH ${c}`).join(", ") || "—"} />
                <DetailRow label="Rows" value={positions.length} />
              </div>
              <div style={{ ...s.tableWrap, marginTop: 8 }}>
                <table style={{ ...s.table, minWidth: 680 }}>
                  <thead><tr>{["Test No", "CH", "Layer", "Required %", "Achieved %", "Status"].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {positions.map((p, i) => {
                      const rc = positionRelativeCompaction(p);
                      const req = num(p.requiredCompaction);
                      const bad = rc != null && req != null && Number.isFinite(req) && req > 0 && rc < req;
                      return (
                        <tr key={i}>
                          <td style={{ ...s.td, fontFamily: tokens.font.mono, fontSize: 12 }}>{p.testNo || "—"}</td>
                          <td style={s.td}>{p.chainage ? `CH ${p.chainage}` : "—"}</td>
                          <td style={s.td}>{LAYER_META[p.layerKey]?.label || p.layerKey || p.layer || "—"}</td>
                          <td style={s.td}>{req != null ? `${req}%` : "—"}</td>
                          <td style={s.td}>{rc != null ? `${rc.toFixed(1)}%` : "—"}</td>
                          <td style={s.td}>{rc == null || req == null ? "Pending" : bad ? <span style={s.pill("fail")}>Below</span> : <span style={s.pill("pass")}>OK</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {test.testType === "ndt" && (
            <>
              <SectionLabel>NDT results</SectionLabel>
              <div style={m.summaryGrid}>
                <DetailRow label="Method" value={test.ndt?.method} />
                <DetailRow label="Weld / Joint No" value={test.ndt?.weldNo} />
                <DetailRow label="Welder" value={test.ndt?.welder} />
                <DetailRow label="Pipe OD / Size" value={test.ndt?.pipeOD} />
                <DetailRow label="Acceptance Standard" value={test.ndt?.acceptanceStandard} />
                <DetailRow label="Result" value={test.ndt?.result} />
              </div>
              <DetailRow label="Defects / Remarks" value={test.ndt?.defects} />
            </>
          )}

          {test.testType === "cube" && (
            <>
              <SectionLabel>Cube results</SectionLabel>
              <div style={m.summaryGrid}>
                <DetailRow label="Structure" value={test.structureRef || test.cube?.element} />
                <DetailRow label="Specified Strength" value={test.cube?.specifiedStrength != null ? `${test.cube.specifiedStrength} MPa` : "—"} />
                <DetailRow label="7-day Average" value={cubeAverage7(test.cube) != null ? `${cubeAverage7(test.cube).toFixed(1)} MPa` : "—"} />
                <DetailRow label="28-day Average" value={cubeAverage28(test.cube) != null ? `${cubeAverage28(test.cube).toFixed(1)} MPa` : "—"} />
              </div>
              <div style={{ ...s.tableWrap, marginTop: 8 }}>
                <table style={{ ...s.table, minWidth: 520 }}>
                  <thead><tr>{["Set", "Lab No", "Age (d)", "Strength"].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                  <tbody>{cubes.map((c, i) => <tr key={i}><td style={s.td}>{c.setLabel}-day</td><td style={{ ...s.td, fontFamily: tokens.font.mono, fontSize: 12 }}>{c.cubeId || "—"}</td><td style={s.td}>{c.ageDays ?? "—"}</td><td style={{ ...s.td, fontFamily: tokens.font.mono }}>{c.strengthMPa != null ? `${Number(c.strengthMPa).toFixed(1)} MPa` : "—"}</td></tr>)}</tbody>
                </table>
              </div>
            </>
          )}

          {test.testType === "pressure" && (
            <>
              <SectionLabel>Pressure / disinfection results</SectionLabel>
              <div style={m.summaryGrid}>
                <DetailRow label="Medium" value={test.pressure?.medium} />
                <DetailRow label="Test Pressure" value={test.pressure?.testPressure != null ? `${test.pressure.testPressure} ${test.pressure?.units || ""}` : "—"} />
                <DetailRow label="Duration" value={test.pressure?.durationMinutes != null ? `${test.pressure.durationMinutes} min` : "—"} />
                <DetailRow label="Allowable Loss" value={test.pressure?.allowableLoss} />
                <DetailRow label="Actual Loss" value={test.pressure?.actualLoss} />
                <DetailRow label="Manual Result" value={test.pressure?.result} />
                <DetailRow label="Disinfection Date" value={test.pressure?.disinfectionDate} />
                <DetailRow label="Bacteriological Result" value={test.pressure?.bacteriologicalResult} />
                <DetailRow label="Clearance Date" value={test.pressure?.clearanceDate} />
              </div>
            </>
          )}

          {test.testType === "dcp" && (
            <>
              <SectionLabel>DCP — in-situ layer strength</SectionLabel>
              <div style={m.summaryGrid}>
                <DetailRow label="Method" value={test.dcp?.method} />
                <DetailRow label="Soil Type" value={test.dcp?.soilType} />
                <DetailRow label="Soil Condition" value={test.dcp?.soilCondition} />
                <DetailRow label="Layer / Level" value={test.dcp?.layerLevel} />
                <DetailRow label="Required CBR" value={test.dcp?.requiredCBR != null && test.dcp?.requiredCBR !== "" ? `${test.dcp.requiredCBR}%` : "—"} />
                <DetailRow label="Achieved CBR" value={test.dcp?.achievedCBR != null && test.dcp?.achievedCBR !== "" ? `${test.dcp.achievedCBR}%` : "—"} />
                <DetailRow label="DN" value={test.dcp?.dn != null && test.dcp?.dn !== "" ? `${test.dcp.dn} mm/blow` : "—"} />
                <DetailRow label="Bearing Capacity" value={test.dcp?.bearingCapacity != null && test.dcp?.bearingCapacity !== "" ? `${test.dcp.bearingCapacity} kPa` : "—"} />
              </div>
            </>
          )}

          <SectionLabel>Notes</SectionLabel>
          <div style={m.notesBox}>{test.notes || "—"}</div>
        </div>

        <div style={m.foot}>
          <span style={s.pill(status)}>Result: {RESULT_COLORS[status].label}</span>
          <button type="button" style={s.primaryBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}



/* ─── Compaction field helper popup ─────────────────────────────────────── */
const COMPACTION_FIELD_HELP = {
  "Test No":       { auto: true,  desc: "Auto-generated sequence number (CT-001, CT-002…). Read-only." },
  "Layer":         { auto: false, desc: "Select the construction layer being tested. Each layer has a different compaction requirement — the Required % pre-fills automatically when you select a layer." },
  "Depth":         { auto: false, desc: "The depth range of the layer being tested, e.g. \"0–300 mm\" or \"300–600 mm\". Enter as a text label." },
  "Required %":    { auto: false, desc: "Minimum relative compaction required for this layer (%). Pre-filled from the layer default but fully editable. Bedding = 90%, Backfill = 93%, Sub-base = 98%, Base Course = 100%." },
  "MDD":           { auto: false, desc: "Maximum Dry Density (kg/m³) from the laboratory Proctor compaction test (SANS 3001-GR30). This value comes from the lab report — it is NOT auto-calculated. Required if you want the Achieved % to auto-derive." },
  "OMC":           { auto: false, desc: "Optimum Moisture Content (%) from the same Proctor test. Informational — not used in the pass/fail calculation." },
  "Dry Density":   { auto: false, desc: "Field dry density measured on site (kg/m³), typically from a nuclear density gauge or sand replacement test. Required if you want Achieved % to auto-derive." },
  "Achieved %":    { auto: true,  desc: "Relative Compaction = (Field Dry Density ÷ MDD) × 100. Auto-derives when both Dry Density and MDD are entered — leave blank to use the auto value, or type directly to override." },
  "Status":        { auto: true,  desc: "Pass if Achieved % ≥ Required %. Pending if either value is missing. Updates live as you type." },
};

function CompactionHelperPopup({ onClose }) {
  return createPortal(
    <div
      style={{ position: "fixed", inset: 0, zIndex: (tokens.z.modal || 1000) + 10, background: "rgba(14,17,22,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 16px", fontFamily: tokens.font.body }}
      onClick={onClose}
    >
      <div
        style={{ width: "min(640px, 100%)", background: C.surface, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: tokens.shadow.lg, overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: `1px solid ${C.border}`, background: C.surfaceAlt }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: C.accent }}>Reference</div>
            <h3 style={{ margin: "4px 0 0", fontSize: 17, fontFamily: tokens.font.display, color: C.ink }}>Compaction row fields</h3>
          </div>
          <button type="button" style={s.ghostBtn} onClick={onClose}>Close</button>
        </div>
        <div style={{ padding: "14px 20px", display: "grid", gap: 10, maxHeight: "70vh", overflowY: "auto" }}>
          <p style={{ margin: 0, fontSize: 12, color: C.inkSubtle, fontStyle: "italic" }}>
            Fields marked <span style={{ background: C.accentSoft, color: C.accent, borderRadius: 4, padding: "1px 6px", fontStyle: "normal", fontWeight: 700 }}>AUTO</span> are calculated or generated — you do not need to type them. All other fields require manual input from the lab or site report.
          </p>
          {Object.entries(COMPACTION_FIELD_HELP).map(([field, info]) => (
            <div key={field} style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 10, alignItems: "start", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.canvas }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 13, color: C.ink }}>{field}</div>
                {info.auto
                  ? <span style={{ display: "inline-block", marginTop: 4, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", background: C.accentSoft, color: C.accent, borderRadius: 4, padding: "2px 6px" }}>AUTO</span>
                  : <span style={{ display: "inline-block", marginTop: 4, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", background: C.warningSoft, color: C.warning, borderRadius: 4, padding: "2px 6px" }}>Manual entry</span>
                }
              </div>
              <div style={{ fontSize: 13, color: C.inkMuted, lineHeight: 1.5 }}>{info.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

function CompactionRowsModal({ positions, setPosition, addPosition, removePosition, onClose }) {
  // State for the "add row" chainage input at the top of each CH group
  const [newChainage, setNewChainage] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);

  // Group positions by chainage — preserve insertion order via Set
  const chainageGroups = useMemo(() => {
    const order = [...new Set(positions.map((p) => p.chainage || ""))];
    return order.map((ch) => ({
      chainage: ch,
      rows: positions.map((p, i) => ({ ...p, _idx: i })).filter((p) => (p.chainage || "") === ch),
    }));
  }, [positions]);

  const computeRC = (p) => {
    const eRC = num(p.relativeCompaction);
    const dRC = (num(p.dryDensity) != null && num(p.mdd)) ? (num(p.dryDensity) / num(p.mdd)) * 100 : null;
    return eRC ?? dRC;
  };
  const overallPass = positions.every((p) => {
    const rc = computeRC(p);
    const req = num(p.requiredCompaction);
    if (rc == null || req == null || req <= 0) return true; // pending rows don't fail
    return rc >= req;
  });
  const anyResult = positions.some((p) => computeRC(p) != null);

  return createPortal(
    <div style={{ ...m.overlay, zIndex: (tokens.z.modal || 1000) + 5 }} onClick={onClose}>
      <div style={{ ...m.wideDialog, maxWidth: 1300 }} onClick={(e) => e.stopPropagation()}>
        {/* ── Header ── */}
        <div style={m.head}>
          <div>
            <div style={m.eyebrow}>Compaction</div>
            <h2 style={m.title}>Result rows — grouped by chainage</h2>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" style={{ ...s.ghostBtn, fontSize: 12 }} onClick={() => setHelpOpen(true)}>? Field guide</button>
            <button type="button" style={s.ghostBtn} onClick={onClose}>Done</button>
          </div>
        </div>

        <div style={{ ...m.body, gap: 16, flex: 1, minHeight: 0 }}>
          {/* ── Add new chainage row ── */}
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", padding: "10px 14px", background: C.surfaceAlt, borderRadius: 10, border: `1px solid ${C.border}` }}>
            <label style={s.label}>
              <span>New chainage (m)</span>
              <input
                style={{ ...s.input, width: 140 }}
                type="number"
                placeholder="e.g. 200"
                value={newChainage}
                onChange={(e) => setNewChainage(e.target.value)}
              />
            </label>
            <button
              type="button"
              style={s.primaryBtn}
              onClick={() => {
                addPosition(newChainage);
                // don't clear — user likely adds multiple rows at same CH
              }}
            >
              + Add row at CH {newChainage || "…"}
            </button>
            <span style={{ fontSize: 12, color: C.inkSubtle, alignSelf: "center" }}>
              {positions.length} row{positions.length === 1 ? "" : "s"} · {chainageGroups.length} chainage{chainageGroups.length === 1 ? "" : "s"}
            </span>
          </div>

          {/* ── Groups ── */}
          <div style={{ display: "grid", gap: 14, alignContent: "start" }}>
            {chainageGroups.length === 0 && (
              <div style={s.empty}>No rows yet. Enter a chainage above and click Add row.</div>
            )}
            {chainageGroups.map(({ chainage, rows }) => {
              const groupPass = rows.every((p) => {
                const eRC = num(p.relativeCompaction);
                const dRC = (num(p.dryDensity) != null && num(p.mdd)) ? (num(p.dryDensity) / num(p.mdd)) * 100 : null;
                const rc = eRC ?? dRC;
                const req = num(p.requiredCompaction);
                if (rc == null || req == null || req <= 0) return true; // pending, not a fail
                return rc >= req;
              });
              const groupAny = rows.some((p) => {
                const eRC = num(p.relativeCompaction);
                const dRC = (num(p.dryDensity) != null && num(p.mdd)) ? (num(p.dryDensity) / num(p.mdd)) * 100 : null;
                return (eRC ?? dRC) != null;
              });
              return (
                <div key={chainage} style={{ border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
                  {/* CH parent header */}
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", background: C.surfaceAlt,
                    borderBottom: `2px solid ${C.border}`,
                  }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      <span style={{ fontWeight: 800, fontSize: 15, color: C.ink, fontFamily: tokens.font.mono }}>
                        CH {chainage || "(no chainage)"}
                      </span>
                      <span style={{ fontSize: 12, color: C.inkSubtle }}>{rows.length} test{rows.length === 1 ? "" : "s"}</span>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {groupAny && (
                        <span style={s.pill(groupPass ? "pass" : "fail")}>
                          {groupPass ? "Pass" : "Fail"}
                        </span>
                      )}
                      <button
                        type="button"
                        style={{ ...s.ghostBtn, fontSize: 11 }}
                        onClick={() => addPosition(chainage)}
                      >
                        + Add layer
                      </button>
                    </div>
                  </div>

                  {/* Child rows table */}
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                      <thead>
                        <tr>
                          {["Test No", "Layer / Activity", "Depth", "Required %", "MDD (kg/m³)", "OMC (%)", "Dry Density", "Achieved %", "Status", ""].map((h) => (
                            <th key={h} style={s.th}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((p) => {
                          // Derive rc from raw string form values (same path as the placeholder)
                          // so status updates live while typing, before the value is saved.
                          const enteredRC = num(p.relativeCompaction);
                          const derivedRC = (num(p.dryDensity) != null && num(p.mdd))
                            ? (num(p.dryDensity) / num(p.mdd)) * 100
                            : null;
                          const rc = enteredRC ?? derivedRC;
                          const req = num(p.requiredCompaction);
                          const bad = rc != null && req != null && Number.isFinite(req) && req > 0 && rc < req;
                          return (
                            <tr key={p._idx} style={{ background: bad ? C.dangerSoft : "transparent" }}>
                              {/* Test No — auto, read-only display */}
                              <td style={{ ...s.td, fontFamily: tokens.font.mono, fontSize: 12, color: C.inkSubtle, whiteSpace: "nowrap" }}>
                                {p.testNo || "—"}
                              </td>

                              {/* Layer dropdown */}
                              <td style={s.td}>
                                <select
                                  style={{ ...s.input, minWidth: 160 }}
                                  value={p.layerKey || "bedding"}
                                  onChange={(e) => {
                                    const meta = LAYER_META[e.target.value];
                                    setPosition(p._idx, {
                                      layerKey: e.target.value,
                                      requiredCompaction: meta?.defaultRequired ?? p.requiredCompaction,
                                    });
                                  }}
                                >
                                  {COMPACTION_LAYERS.map((l) => (
                                    <option key={l.key} value={l.key}>{l.label}</option>
                                  ))}
                                </select>
                              </td>

                              {/* Depth */}
                              <td style={s.td}>
                                <TextInput
                                  value={p.depth}
                                  onChange={(e) => setPosition(p._idx, { depth: e.target.value })}
                                  placeholder="0–300 mm"
                                  style={{ minWidth: 90 }}
                                />
                              </td>

                              {/* Required % — pre-filled from layer default, editable */}
                              <td style={s.td}>
                                <TextInput
                                  type="number"
                                  value={p.requiredCompaction}
                                  onChange={(e) => setPosition(p._idx, { requiredCompaction: e.target.value })}
                                  placeholder="e.g. 93"
                                  style={{ minWidth: 70, fontWeight: 700 }}
                                />
                              </td>

                              {/* MDD */}
                              <td style={s.td}>
                                <TextInput
                                  type="number"
                                  value={p.mdd}
                                  onChange={(e) => setPosition(p._idx, { mdd: e.target.value })}
                                  placeholder="Proctor (kg/m³)"
                                  style={{ minWidth: 100 }}
                                />
                              </td>

                              {/* OMC */}
                              <td style={s.td}>
                                <TextInput
                                  type="number"
                                  value={p.omc}
                                  onChange={(e) => setPosition(p._idx, { omc: e.target.value })}
                                  placeholder="%"
                                  style={{ minWidth: 70 }}
                                />
                              </td>

                              {/* Dry density */}
                              <td style={s.td}>
                                <TextInput
                                  type="number"
                                  value={p.dryDensity}
                                  onChange={(e) => setPosition(p._idx, { dryDensity: e.target.value })}
                                  placeholder="kg/m³"
                                  style={{ minWidth: 80 }}
                                />
                              </td>

                              {/* Achieved % */}
                              <td style={s.td}>
                                <TextInput
                                  type="number"
                                  value={p.relativeCompaction}
                                  onChange={(e) => setPosition(p._idx, { relativeCompaction: e.target.value })}
                                  placeholder={
                                    num(p.dryDensity) != null && num(p.mdd)
                                      ? `${((num(p.dryDensity) / num(p.mdd)) * 100).toFixed(1)} (auto)`
                                      : "e.g. 98.5"
                                  }
                                  style={{
                                    minWidth: 80,
                                    color: bad ? C.danger : C.ink,
                                    fontWeight: bad ? 800 : 500,
                                  }}
                                />
                              </td>

                              {/* Status chip */}
                              <td style={s.td}>
                                {rc == null || !Number.isFinite(req)
                                  ? <span style={{ fontSize: 12, color: C.inkSubtle }}>Pending</span>
                                  : bad
                                    ? <span style={s.pill("fail")}>Below</span>
                                    : <span style={s.pill("pass")}>OK</span>
                                }
                              </td>

                              {/* Remove */}
                              <td style={s.td}>
                                {positions.length > 1 && (
                                  <button type="button" style={s.dangerBtn} onClick={() => removePosition(p._idx)}>×</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={m.foot}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            {anyResult && <span style={s.pill(overallPass ? "pass" : "fail")}>Overall: {overallPass ? "Pass" : "Fail"}</span>}
            <span style={{ fontSize: 12, color: C.inkSubtle }}>{positions.length} row{positions.length === 1 ? "" : "s"}</span>
          </div>
          <button type="button" style={s.primaryBtn} onClick={onClose}>Done</button>
        </div>
        {helpOpen && <CompactionHelperPopup onClose={() => setHelpOpen(false)} />}
      </div>
    </div>,
    document.body
  );
}

function SectionLabel({ children }) {
  return <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: C.inkSubtle, margin: "14px 0 2px" }}>{children}</div>;
}

/** Coerce string inputs to numbers + collapse point chainage into start/end. */
function normalizeForm(form) {
  const mode = TEST_TYPE_META[form.testType]?.locationMode;
  const cs = num(form.chainageStart);
  const ce = num(form.chainageEnd);

  let chainageStart = cs;
  let chainageEnd = ce;
  if (mode === "point") { chainageEnd = cs; }
  if (mode === "range" && cs != null && ce != null && ce < cs) { chainageStart = ce; chainageEnd = cs; }

  return {
    testType: form.testType,
    title: form.title?.trim() || "",
    testDate: form.testDate || "",
    lab: form.lab?.trim() || "",
    reportNo: form.reportNo?.trim() || "",
    technician: form.technician?.trim() || "",
    documentRef: form.documentRef?.trim() || "",
    notes: form.notes || "",
    assetId: form.assetId || "",
    assetName: form.assetName || "",
    side: form.side || "",
    structureRef: form.structureRef || "",
    chainageStart,
    chainageEnd,
    locationMode: mode,
    compaction: {
      spec: form.compaction.spec || "",
      positions: (form.compaction.positions || []).map((p) => ({
        testNo: p.testNo || "",
        chainage: p.chainage || "",
        layerKey: p.layerKey || "bedding",
        depth: p.depth || "",
        requiredCompaction: num(p.requiredCompaction),
        wetDensity: num(p.wetDensity),
        dryDensity: num(p.dryDensity),
        moistureCorrected: num(p.moistureCorrected),
        mdd: num(p.mdd),
        omc: num(p.omc),
        relativeCompaction: num(p.relativeCompaction) ?? (num(p.dryDensity) != null && num(p.mdd) ? (num(p.dryDensity) / num(p.mdd)) * 100 : null),
      })),
    },
    ndt: { ...form.ndt },
    cube: {
      ...form.cube,
      specifiedStrength: num(form.cube.specifiedStrength),
      cubes: (form.cube.cubes || []).map((c) => ({
        setLabel: String(c.setLabel),
        cubeId: c.cubeId || "",
        ageDays: num(c.ageDays),
        loadKN: num(c.loadKN),
        factor: num(c.factor) || 10,
        strengthMPa: num(c.strengthMPa) ?? (num(c.loadKN) != null ? num(c.loadKN) / (num(c.factor) || 10) : null),
      })),
    },
    pressure: {
      ...form.pressure,
      testPressure: num(form.pressure.testPressure),
      durationMinutes: num(form.pressure.durationMinutes),
      allowableLoss: num(form.pressure.allowableLoss),
      actualLoss: num(form.pressure.actualLoss),
    },
    dcp: {
      method: form.dcp?.method?.trim() || "",
      soilType: form.dcp?.soilType || "",
      soilCondition: form.dcp?.soilCondition || "",
      layerLevel: form.dcp?.layerLevel?.trim() || "",
      requiredCBR: num(form.dcp?.requiredCBR),
      achievedCBR: num(form.dcp?.achievedCBR),
      dn: num(form.dcp?.dn),
      bearingCapacity: num(form.dcp?.bearingCapacity),
    },
  };
}

const m = {
  overlay: { position: "fixed", inset: 0, zIndex: tokens.z.modal, background: "rgba(14,17,22,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "32px 16px", overflowY: "auto", fontFamily: tokens.font.body },
  dialog: { width: "min(960px, 100%)", background: C.surface, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: tokens.shadow.lg, display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 64px)" },
  wideDialog: { width: "min(1240px, 100%)", background: C.surface, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: tokens.shadow.lg, display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 64px)" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "18px 22px", borderBottom: `1px solid ${C.border}` },
  eyebrow: { fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: C.accent },
  title: { margin: "4px 0 0", fontSize: 20, fontFamily: tokens.font.display, color: C.ink },
  body: { padding: "16px 22px", overflowY: "auto", display: "grid", gap: 10 },
  foot: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "14px 22px", borderTop: `1px solid ${C.border}`, background: C.surfaceAlt, borderRadius: "0 0 16px 16px", flexWrap: "wrap" },
  grid3: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, minWidth: 0 },
  hint: { margin: "6px 0 0", fontSize: 12, color: C.inkSubtle, fontStyle: "italic" },
  compactionSummaryCard: { marginTop: 8, padding: 12, border: `1px solid ${C.border}`, borderRadius: 10, background: C.surfaceAlt },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 },
  detailRow: { border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", background: C.surfaceAlt, minWidth: 0 },
  detailLabel: { fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: C.inkSubtle, marginBottom: 5 },
  detailValue: { fontSize: 13, color: C.ink, wordBreak: "break-word" },
  notesBox: { minHeight: 54, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, background: C.surfaceAlt, color: C.inkMuted, whiteSpace: "pre-wrap" },
};